/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useCallback, useRef } from "react";
import { isFunction } from "@adapptio/lib-shared";

export function useDebounce<
	Args extends unknown[] = unknown[],
	Result extends unknown = unknown,
	Fun extends (...args: Args) => Result = () => Result
>(fun: Fun, timeout: number): [debounced: (...args: Args) => Promise<Result>, isDebouncing: () => boolean] {
	const debounceTimeoutId = useRef<ReturnType<typeof setTimeout>>(null);

	const debounced = useCallback<(...args: Args) => Promise<Result>>(
		(...args) =>
			new Promise((resolve) => {
				clearTimeout(debounceTimeoutId.current);

				debounceTimeoutId.current = setTimeout(() => {
					if (isFunction(fun)) {
						resolve(fun(...args));
					}

					debounceTimeoutId.current = null;
				}, timeout);
			}),
		[ fun, timeout ]
	);

	const isDebouncing = useCallback(() => !!debounceTimeoutId.current, []);

	return [ debounced, isDebouncing ];
}
